import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { routes } from './app.routes';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth } from '@angular/fire/auth';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { correlationIdInterceptor } from './core/interceptors/correlation-id.interceptor';
import { httpErrorLoggerInterceptor } from './core/interceptors/http-error-logger.interceptor';
import { GlobalErrorHandler } from './core/errors/global-error-handler';
import { BackOriginService } from './core/services/back-origin.service';
import { QueryParamScrollService } from './core/services/query-param-scroll.service';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { environment } from '../environments/environment';

export function createAppConfig(): ApplicationConfig {
  const firebaseConfig = environment.firebase;
  const useFirebaseEmulator = environment.firebase.useEmulator;

  return {
    providers: [
      provideBrowserGlobalErrorListeners(),
      // Scroll handling lives in QueryParamScrollService: query-parameter-only
      // navigations (e.g. URL-backed filters) keep the scroll position.
      provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'disabled' })),
      provideEnvironmentInitializer(() => {
        inject(BackOriginService);
        inject(QueryParamScrollService);
      }),
      provideAnimationsAsync(),
      { provide: ErrorHandler, useClass: GlobalErrorHandler },
      provideHttpClient(
        withInterceptors([correlationIdInterceptor, authInterceptor, httpErrorLoggerInterceptor]),
      ),
      provideFirebaseApp(() => initializeApp(firebaseConfig)),
      provideAuth(() => {
        const auth = getAuth();
        if (useFirebaseEmulator && environment.firebase.emulators) {
          connectAuthEmulator(auth, environment.firebase.emulators.authUrl, {
            disableWarnings: true,
          });
        }
        return auth;
      }),
    ],
  };
}
